import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  HomeIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import ApiCustom from "../../utils/ApiCustom";

/**
 * API: GET /get-media-public/cooperation
 * Res: { data: { images: [{ id, url, ...}] } }
 */
const ImageGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed
  const lightboxRef = useRef(null);

  // Fetch images
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const { data } = await ApiCustom.get("/get-media-public/cooperation");
        if (!active) return;
        const list = data?.data?.images || [];
        setImages(list);
      } catch (e) {
        setErr(
          e?.response?.data?.message || e?.message || "Lỗi không xác định",
        );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => (active = false);
  }, []);

  const openAt = useCallback((idx) => {
    setLightboxIndex(idx);
  }, []);

  const close = useCallback(() => {
    setLightboxIndex(null);
  }, []);

  const prev = useCallback(() => {
    setLightboxIndex((i) => {
      if (i === null) return i;
      return (i - 1 + images.length) % images.length;
    });
  }, [images.length]);

  const next = useCallback(() => {
    setLightboxIndex((i) => {
      if (i === null) return i;
      return (i + 1) % images.length;
    });
  }, [images.length]);

  // Lock scroll & keyboard shortcuts while lightbox open
  useEffect(() => {
    const onKey = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    if (lightboxIndex !== null) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIndex, close, prev, next]);

  return (
    <div className="container mx-auto">
      {/* Breadcrumb */}
      <nav className="flex text-gray-500 text-sm mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 flex-wrap">
          <li className="inline-flex items-center">
            <Link
              to="/"
              className="inline-flex items-center hover:text-blue-600 font-medium"
            >
              <HomeIcon className="w-4 h-4 mr-2 text-gray-400" />
              Trang chủ
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="w-3 h-3 mx-2 text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M7.05 4.05a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L11.19 10 7.05 5.91a.75.75 0 010-1.06z" />
              </svg>
              <span className="ml-1 text-gray-400 font-medium">Hình ảnh</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* States */}
      {loading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="animate-pulse bg-base-200 rounded-xl aspect-[4/3]"
            />
          ))}
        </div>
      )}

      {!loading && err && (
        <div className="alert alert-error shadow-lg">
          <span>Lỗi tải hình: {err}</span>
        </div>
      )}

      {!loading && !err && (!images || images.length === 0) && (
        <div className="alert alert-info shadow-lg">
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              className="stroke-current flex-shrink-0 w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <span>Không có hình nào được tìm thấy.</span>
          </div>
        </div>
      )}

      {/* Grid */}
      {!loading && !err && images && images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, idx) => (
            <button
              key={img.id ?? idx}
              onClick={() => openAt(idx)}
              className="group relative rounded-xl overflow-hidden bg-base-200 focus:outline-none focus:ring-2 focus:ring-primary/60"
              aria-label={`Mở ảnh ${idx + 1} trong lightbox`}
            >
              <div className="aspect-[4/3] w-full">
                <img
                  src={img.url}
                  alt={`cooperation-${img.id ?? idx}`}
                  loading="lazy"
                  className="w-full h-full object-contain bg-white transition-transform duration-300 group-hover:scale-[1.02]"
                  referrerPolicy="strict-origin-when-cross-origin"
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml;charset=utf-8," +
                      encodeURIComponent(
                        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 225"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="#9ca3af">Không tải được ảnh</text></svg>',
                      );
                  }}
                />
              </div>
              {/* Subtle overlay */}
              <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/10 to-transparent" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && images[lightboxIndex] && (
        <div
          ref={lightboxRef}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          role="dialog"
          aria-modal="true"
          aria-label="Xem ảnh phóng to"
          onClick={(e) => {
            // close when clicking backdrop
            if (e.target === lightboxRef.current) close();
          }}
        >
          {/* Image container */}
          <div className="relative w-full h-full p-4 md:p-8 flex items-center justify-center">
            {/* Close */}
            <button
              onClick={close}
              aria-label="Đóng"
              className="absolute top-4 right-4 md:top-6 md:right-6 rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 p-2"
            >
              <XMarkIcon className="w-7 h-7 text-white" />
            </button>

            {/* Prev */}
            {images.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="Ảnh trước"
                className="absolute left-2 md:left-6 rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 p-2"
              >
                <ChevronLeftIcon className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Next */}
            {images.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="Ảnh sau"
                className="absolute right-2 md:right-6 rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 p-2"
              >
                <ChevronRightIcon className="w-8 h-8 text-white" />
              </button>
            )}

            {/* Big image */}
            <img
              src={images[lightboxIndex].url}
              alt={`cooperation-${images[lightboxIndex].id ?? lightboxIndex}`}
              className="max-h-[85vh] max-w-[95vw] object-contain select-none"
              draggable={false}
              referrerPolicy="strict-origin-when-cross-origin"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
